import Phaser from 'phaser';
import GameConfig from './GameConfig';
import PhysicsPanel from './PhysicsPanel';
import StatusPanel from './StatusPanel';
import { createLevel } from './level';
import { createCollectibles, updateCollectibles } from './collectibles';
import { createLadderBlockers } from './ladders';
import { createPlayer, updatePlayer } from './player';
import { createCactus, updateCactus } from './cactus';
import { createSpiders, updateSpiders } from './spider';
import { createRoaches, updateRoaches } from './roach';
import { createBombs, updateBombs } from './bomb';
import { createCroc, updateCroc } from './croc';
import { playDeathMusic, showGameOver } from './death';

const config = {
  type: Phaser.AUTO,
  width: GameConfig.SCREEN_WIDTH,
  height: GameConfig.SCREEN_HEIGHT,
  zoom: 1,
  backgroundColor: '#ffffff',
  pixelArt: true,
  roundPixels: true,
  scale: {
    mode: Phaser.Scale.NONE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  physics: {
    default: 'arcade',
    arcade: { gravity: { y: 2400 }, debug: false },
  },
  scene: { preload, create, update },
};

const game = new Phaser.Game(config); // eslint-disable-line no-unused-vars

function preload() {
  this.load.image('env', 'assets/tilesets/FK_Sewer_Environment_SpriteSheet.png');
  this.load.tilemapTiledJSON('level0', 'assets/maps/level0.0.4.json');
  this.load.spritesheet('door',     'assets/tilesets/FK_Doors_SpriteSheet_01.png',       { frameWidth: 32, frameHeight: 48 });
  this.load.spritesheet('rat',      'assets/Rat/FK_Rat_SpriteSheet_01.png',              { frameWidth: 66, frameHeight: 43 });
  this.load.spritesheet('powerups', 'assets/tilesets/FK_PowerUps_SpriteSheet_01.png',    { frameWidth: 16, frameHeight: 16 });
  this.load.spritesheet('status',   'assets/tilesets/FK_StatusElements_SpriteSheet_01.png', { frameWidth: 16, frameHeight: 16 });
  this.load.spritesheet('cacti',  'assets/Enemies/FK_Enemy_Cacti_SpriteSheet.png',   { frameWidth: 37, frameHeight: 37 });
  this.load.spritesheet('spider', 'assets/Enemies/FK_Enemy_Spider_SpriteSheet.png', { frameWidth: 17, frameHeight: 16 });
  this.load.spritesheet('roach',  'assets/Enemies/FK_Enemy_Roach_SpriteSheet.png',  { frameWidth: 38, frameHeight: 18 });
  this.load.spritesheet('bomb',   'assets/Enemies/FK_Enemy_Bomb_SpriteSheet.png',   { frameWidth: 33, frameHeight: 33 });
  this.load.spritesheet('croc',   'assets/Enemies/FK_FinalBoss_Croc_SpriteSheet.png', { frameWidth: 116, frameHeight: 66 });
  this.load.binary('square1',    'assets/music/sewer/Slinky-SQ1.2.0.mid');
  this.load.binary('square2',    'assets/music/sewer/Slinky-SQ2.2.0.mid');
  this.load.binary('triangle',   'assets/music/sewer/Slinky-TRI.2.0.mid');
  this.load.binary('noise',      'assets/music/sewer/Slinky-NOISE.2.0.mid');
  this.load.binary('death-mel1', 'assets/music/death/mel1.mid');
  this.load.binary('death-mel2', 'assets/music/death/mel2.mid');
  this.load.binary('death-bass', 'assets/music/death/bass.mid');
}

function create() {
  const { map, environmentLayer } = createLevel(this);
  createCollectibles(this, map);
  createPlayer(this);
  createLadderBlockers(this, environmentLayer, map);
  createCactus(this);
  createSpiders(this);
  createRoaches(this);
  createBombs(this);
  createCroc(this);

  const startData = this.scene.settings.data;
  const startLives = startData?.lives ?? 4;
  this.statusPanel = new StatusPanel(this, this.player, startLives);
  this.physicsPanel = new PhysicsPanel(this);
  if (startData?.musicEnabled) this.physicsPanel.toggleSound();
}

function update(time, delta) {
  if (this.player.isDead) return;

  // Pit death — player fell below the floor
  if (this.player.y > this.pitDeathY) {
    this.playerInvincible = false;
    this.player.health = 0;
    this.player.isDead = true;
    this.player.setVisible(false);
    this.player.setVelocity(0, 0);
    this.player.body.setAllowGravity(false);
    (this.cacti || []).filter(c => c.active).forEach(c => c.setVelocityX(0));
    if (this.backgroundMusic) { this.backgroundMusic.stopParts(); this.backgroundMusic.pause(); }
    const worldObjects = [
      this.environmentLayer,
      ...(this.cacti || []).filter(c => c.active),
      ...(this.croc?.active ? [this.croc] : []),
      ...(this.spiders || []).filter(s => s.active),
      ...(this.roaches || []).filter(r => r.active),
      ...this.coins.getChildren(),
      ...this.hearts.getChildren(),
      ...this.slingshots.getChildren(),
      ...this.oneUps.getChildren(),
      ...this.doors.getChildren(),
    ];
    this.tweens.add({ targets: worldObjects, alpha: 0, duration: 400 });
    const cam = this.cameras.main;
    const overlay = this.add.rectangle(0, 0, cam.width, cam.height, 0xffffff)
      .setOrigin(0, 0).setScrollFactor(0).setDepth(10).setAlpha(0);
    playDeathMusic(this, () => {
      this.tweens.add({
        targets: overlay,
        alpha: 1,
        duration: 600,
        onComplete: () => {
          const lives = Math.max(0, this.statusPanel.lives - 1);
          if (lives <= 0) {
            showGameOver(this);
          } else {
            if (this.backgroundMusic) { this.backgroundMusic.dispose(); this.backgroundMusic = null; }
            if (this.deathMusic) this.deathMusic.stop();
            if (this.physicsPanel) this.physicsPanel.destroy();
            this.scene.restart({ lives, musicEnabled: this.isMusicPlaying });
          }
        }
      });
    });
    return;
  }

  updateCollectibles(this);
  this.statusPanel.update();

  if (Phaser.Input.Keyboard.JustDown(this.inputKeys.toggleMusic) ||
      Phaser.Input.Keyboard.JustDown(this.inputKeys.toggleMusicAlt)) {
    this.physicsPanel.toggleSound();
  }

  updateCactus(this, delta);
  updateSpiders(this, time);
  updateRoaches(this);
  updateBombs(this);
  updateCroc(this, delta);
  updatePlayer(this, time, delta);
}
